import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS_FR } from "@/lib/time";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, BarChart, Bar, Legend } from "recharts";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-muted text-muted-foreground" },
  submitted: { label: "En attente", cls: "bg-primary/10 text-primary border border-primary/30" },
  approved: { label: "Validée", cls: "bg-secondary text-secondary-foreground border border-secondary" },
  rejected: { label: "Refusée", cls: "bg-destructive/10 text-destructive border border-destructive/30" },
};

export default function HistoryPage() {
  const [history, setHistory] = useState({ weeks: [], cumul_recup_hours: 0 });
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/timesheets/history", { params: { months: 12 } })
      .then(r => setHistory(r.data))
      .catch(e => toast.error(extractError(e)));
  }, []);

  useEffect(() => {
    api.get("/timesheets/summary", { params: { year } })
      .then(r => setSummary(r.data))
      .catch(e => toast.error(extractError(e)));
  }, [year]);

  const monthChartData = summary ? summary.months.map(m => ({
    mois: MONTHS_FR[m.month - 1].slice(0, 3),
    Heures: m.worked_hours,
    Delta: m.delta_hours,
  })) : [];

  return (
    <div className="space-y-6" data-testid="history-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Historique</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Cumul & récupérations</h1>
        <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble de vos heures et de votre solde de récupération.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Cumul récup (validées)</div>
            <div className={`font-display text-4xl font-semibold tabular-nums mt-1 ${history.cumul_recup_hours >= 0 ? "text-primary" : "text-destructive"}`} data-testid="cumul-recup-hours">
              {history.cumul_recup_hours >= 0 ? "+" : ""}{history.cumul_recup_hours.toFixed(2)} h
            </div>
            <div className="text-xs text-muted-foreground mt-1">Sur les 12 derniers mois</div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total heures {year}</div>
            <div className="font-display text-4xl font-semibold tabular-nums mt-1">
              {summary?.total_worked_hours?.toFixed(1) || 0} h
            </div>
            <div className="text-xs text-muted-foreground mt-1">Toutes semaines confondues</div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Année</div>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="mt-2 rounded-full" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[year - 2, year - 1, year, year + 1].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Cumul récupération (12 mois glissants)</h2>
          <div className="h-64" data-testid="cumul-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history.weeks}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="week_label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))" }} />
                <Line type="monotone" dataKey="cumul_recup_hours" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} name="Cumul récup (h)" />
                <Line type="monotone" dataKey="worked_hours" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={{ r: 2 }} name="Heures / semaine" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Vue mensuelle — {year}</h2>
          <div className="h-64" data-testid="month-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthChartData}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))" }} />
                <Legend />
                <Bar dataKey="Heures" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Delta" fill="hsl(var(--chart-3))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Détail des semaines</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="py-2 font-medium">Semaine</th>
                  <th className="py-2 font-medium">Statut</th>
                  <th className="py-2 font-medium text-right">Heures</th>
                  <th className="py-2 font-medium text-right">Delta</th>
                  <th className="py-2 font-medium text-right">Cumul récup</th>
                </tr>
              </thead>
              <tbody>
                {history.weeks.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Aucune semaine enregistrée</td></tr>
                )}
                {[...history.weeks].reverse().map((w) => {
                  const st = STATUS[w.status] || STATUS.draft;
                  return (
                    <tr key={w.week_start} className="border-b border-border/40 fade-up" data-testid={`history-row-${w.week_start}`}>
                      <td className="py-3 font-medium">{w.week_label}<div className="text-xs text-muted-foreground">{w.week_start}</div></td>
                      <td className="py-3"><Badge className={`${st.cls} rounded-full px-2.5`}>{st.label}</Badge></td>
                      <td className="py-3 text-right tabular-nums">{w.worked_hours.toFixed(2)}</td>
                      <td className={`py-3 text-right tabular-nums ${w.delta_hours >= 0 ? "text-primary" : "text-destructive"}`}>
                        {w.delta_hours >= 0 ? "+" : ""}{w.delta_hours.toFixed(2)}
                      </td>
                      <td className={`py-3 text-right tabular-nums font-medium ${w.cumul_recup_hours >= 0 ? "text-primary" : "text-destructive"}`}>
                        {w.cumul_recup_hours >= 0 ? "+" : ""}{w.cumul_recup_hours.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
